import { action, makeAutoObservable, observable, runInAction } from 'mobx';
import {
  _ClassesQuery,
  _PeopleQuery,
  _RoomsQuery,
  _SubjectContainersQuery,
  _SubjectsQuery,
  AssignTeachersToLessonsDocument,
  AssignTeachersToLessonsMutation,
  AssignTeachersToLessonsMutationVariables,
  ConnectLessonsToTimetableVersionDocument,
  ConnectLessonsToTimetableVersionMutation,
  ConnectLessonsToTimetableVersionMutationVariables,
  CopyLessonsToTimetableVersionDocument,
  CopyLessonsToTimetableVersionMutation,
  CopyLessonsToTimetableVersionMutationVariables,
  CreateLessonInTimetableVersionDocument,
  CreateLessonInTimetableVersionMutation,
  CreateLessonInTimetableVersionMutationVariables,
  DeleteLessonsDocument,
  DeleteLessonsMutation,
  DeleteLessonsMutationVariables,
  DeleteTimetableCardsDocument,
  DeleteTimetableCardsMutation,
  DeleteTimetableCardsMutationVariables,
  DetachLessonsFromTimetableVersionDocument,
  DetachLessonsFromTimetableVersionMutation,
  DetachLessonsFromTimetableVersionMutationVariables,
  DetachTeachersFromLessonsDocument,
  DetachTeachersFromLessonsMutation,
  DetachTeachersFromLessonsMutationVariables,
  LessonEditStatus,
  LessonFlag,
  LessonItemFragment,
  LessonsByUuidsDocument,
  LessonsByUuidsQuery,
  LessonsByUuidsQueryVariables,
  MergeLessonsDocument,
  MergeLessonsMutation,
  MergeLessonsMutationVariables,
  SetLessonsFlagDocument,
  SetLessonsFlagMutation,
  SetLessonsFlagMutationVariables,
  TimetableCardsForLessonDocument,
  TimetableCardsForLessonQuery,
  TimetableCardsForLessonQueryVariables,
} from '../../../types/planung-graphql-client-defs';
import { LessonInfo } from '../../../utils/calculateLessonInfos';
import { urqlClient } from '../../../utils/urqlClient';
import { toLessonFormValues, toLessonInput } from '../utils/buildLessonInput';
import { lessonResponseToTableType } from '../utils/lessonResponseToTableType';
import { CardsInfo, CardsInfoLesson, ClassInterfaceType, LessonTableType } from './Table/TimetableVersionLessonsTable';

/**
 * Diese Mutationen liefern nur uuids bzw. eine Anzahl zurück, keine Zuordnungen. Ohne die Typnamen merkt der
 * urql-Cache nicht, dass sich die Zuordnungen der Version geändert haben, und z.B. das Bearbeiten-Formular
 * (liest lessonsForTimetableVersion) findet eine gerade in die Version kopierte Zuordnung nicht.
 */
const LESSON_LIST_TYPENAMES = ['LessonItem', 'Lesson', 'TimetableCard', 'ClassGridCard'];

export type VersionLessonTableType = {
  uuid: string;
  subject: string;
  subjectShortName: string;
  classSort: string;
  teacherSort?: string;
  subjectUuid: string;
  editStatus?: LessonEditStatus;
  cardsInfos?: {
    defaultCards: CardsInfo[][];
    subjectContainerCards: CardsInfo[][];
  };
  classes: Array<
    ClassInterfaceType & {
      groups?: Array<{ uuid: string; shortName?: string; name: string; classFraction?: number }>;
    }
  >;
  classesUuids: string[];
  teachers: {
    uuid: string;
    name?: string | null;
    color?: string | null;
  }[];
  teacherUuids: string[];
  elective: boolean;
  teachingLoadEnabled: boolean;
  timetableEnabled: boolean;
  onlyInTimetableVersion: boolean;
  lessonUnits?: {
    uuid: string;
    duration: number;
    count: number;
    subjectContainer?: { uuid: string; name: string; shortName: string } | null;
  }[];
  lessonInfo?: LessonInfo;
  groupClassNames?: string;
  teachingLoadFactors?: string;
  placedCardsCount?: number | null;
  isSubjectContainerLesson?: boolean;
  curriculumUuid?: string;
  schoolYearUuid?: string;
  rooms?: { uuid: string; name: string }[];
  roomsUuids?: string[];
  cardInfoLesson?: CardsInfoLesson;
};

export class TimetableVersionLessonStore {
  public lessons = new Map<string, VersionLessonTableType>();

  private _teachers: Pick<_PeopleQuery, 'people'>['people'] = [];
  private _classes: Pick<_ClassesQuery, 'classes'>['classes'] = [];
  private _subjects: Pick<_SubjectsQuery, 'subjects'>['subjects'] = [];
  private _subjectContainers: Pick<_SubjectContainersQuery, 'subjectContainers'>['subjectContainers'] = [];
  private _rooms: Pick<_RoomsQuery, 'rooms'>['rooms'] = [];

  private _ensureLoadedCallback: (() => void) | null = null;
  private _reloadLessonsCallback: (() => void) | null = null;

  constructor() {
    makeAutoObservable(this, {
      lessons: observable.shallow,
      assignLessons: action.bound,
    });
  }

  /** Registriert den Callback, der das Lazy-Loading im Provider auslöst. */
  setEnsureLoaded(callback: () => void) {
    this._ensureLoadedCallback = callback;
  }

  /** Wird von Tab-Komponenten aufgerufen, wenn Lesson-Daten benötigt werden. */
  ensureLoaded() {
    this._ensureLoadedCallback?.();
  }

  /** Registriert den Callback, der die Lesson-Query im Provider neu ausführt. */
  setReloadLessons(callback: () => void) {
    this._reloadLessonsCallback = callback;
  }

  /**
   * Lesson-Liste frisch vom Server holen. Nötig nach Änderungen, die NICHT über GraphQL laufen
   * (z.B. der ASC-Import per REST): urql sieht sie nicht und invalidiert seinen Cache nicht,
   * die Liste bliebe sonst stale — inklusive gelöschter Unterrichte.
   */
  reloadLessons() {
    this._reloadLessonsCallback?.();
  }

  removeLesson(lessonUuid: string) {
    this.lessons.delete(lessonUuid);
  }

  removeLessons(lessonUuids: string[]) {
    lessonUuids.forEach((uuid) => {
      this.lessons.delete(uuid);
    });
  }

  addLessons(lessons: VersionLessonTableType[]) {
    lessons.forEach((lesson) => {
      this.addLesson(lesson);
    });
  }

  addLesson(lesson: VersionLessonTableType) {
    if (this.lessons.has(lesson.uuid)) {
      this.lessons.set(lesson.uuid, { ...this.lessons.get(lesson.uuid), ...lesson });
    } else {
      this.lessons.set(lesson.uuid, { ...lesson });
    }
  }

  async addLessonsByUuids(lessonUuids: string[]) {
    const versionLesson = await this.lessonRequest({ uuids: lessonUuids });
    if (versionLesson) {
      versionLesson.forEach((lesson) => {
        this.addLesson({
          ...lessonResponseToTableType({
            queryLessonsData: [lesson],
            deputateByTeachersData: [], // TODO: add from backend-resolver
            versionContext: true,
            isTeachingBlock: false,
            subjectsData: this._subjects,
            subjectContainerData: this._subjectContainers,
          })[0],
        });
      });
    }
  }

  /**
   * Nimmt bestehende Lessons in eine Timetable-Version auf: führt die connect-Mutation aus
   * und lädt die betroffenen Lessons frisch in den Store nach. Kapselt Write + Read-back +
   * State-Update an einer Stelle (analog zu moveLessonToVersion/copyLessonsToTimetableVersion).
   */
  async addLessonsToVersion(lessonUuids: string[], versionUuid: string, organizationUuid: string) {
    const res = await urqlClient
      .mutation<ConnectLessonsToTimetableVersionMutation, ConnectLessonsToTimetableVersionMutationVariables>(
        ConnectLessonsToTimetableVersionDocument,
        {
          organizationUuid,
          timetableVersionUuid: versionUuid,
          lessonUuids,
        },
        { additionalTypenames: LESSON_LIST_TYPENAMES },
      )
      .toPromise();

    if (res.error || !res.data?.connectLessonsToTimetableVersion) {
      throw new Error(`Failed to add lessons to timetable version: ${res.error?.message}`);
    }

    const nodeUuids = res.data.connectLessonsToTimetableVersion.nodeUuids;
    await this.addLessonsByUuids(nodeUuids);
    return nodeUuids;
  }

  /**
   * Importiert bestehende Lessons als eigenständige Kopien in die Version (copyLessonsToTimetableVersion).
   * Im Gegensatz zu addLessonsToVersion (verknüpfen) bleibt KEINE Referenz auf die Deputats-Zuordnung
   * bestehen – die kopierten Lessons sind unabhängig editierbar.
   */
  async importLessonsToVersion(lessonUuids: string[], versionUuid: string, organizationUuid: string) {
    const newLessons = await this.copyLessonsToTimetableVersionRequest({
      lessonUuids,
      timetableVersionUuid: versionUuid,
      organizationUuid,
    });
    runInAction(() => {
      newLessons.forEach((lesson) => this.addLesson(lesson));
    });
    return newLessons.map((lesson) => lesson.uuid);
  }

  setLessons(_lessons: LessonTableType[]) {
    const lessons = _lessons.sort((a, b) => {
      const classSortA = a.classSort || '';
      const classSortB = b.classSort || '';
      return classSortA.localeCompare(classSortB);
    });
    this.lessons.clear();
    lessons.forEach((lesson) => {
      this.lessons.set(lesson.uuid, { ...lesson });
    });
  }

  setBasicData({
    teachers,
    classes,
    subjects,
    rooms,
    subjectContainers,
  }: {
    teachers?: Pick<_PeopleQuery, 'people'>['people'];
    classes?: Pick<_ClassesQuery, 'classes'>['classes'];
    subjects?: Pick<_SubjectsQuery, 'subjects'>['subjects'];
    rooms?: Pick<_RoomsQuery, 'rooms'>['rooms'];
    subjectContainers?: Pick<_SubjectContainersQuery, 'subjectContainers'>['subjectContainers'];
  }) {
    if (teachers) {
      this._teachers = teachers;
    }
    if (classes) {
      this._classes = classes;
    }
    if (subjects) {
      this._subjects = subjects;
    }
    if (rooms) {
      this._rooms = rooms;
    }
    if (subjectContainers) {
      this._subjectContainers = subjectContainers;
    }
  }

  async updateCheckboxes({
    lessonUuid,
    type,
    value,
  }: {
    lessonUuid: string;
    type: 'teachingLoadEnabled' | 'timetableEnabled' | 'elective';
    value: boolean;
  }) {
    const flags = {
      teachingLoadEnabled: LessonFlag.TeachingLoadEnabled,
      timetableEnabled: LessonFlag.TimetableEnabled,
      elective: LessonFlag.Elective,
    };
    const response = await urqlClient
      .mutation<SetLessonsFlagMutation, SetLessonsFlagMutationVariables>(SetLessonsFlagDocument, {
        uuids: [lessonUuid],
        flag: flags[type],
        value,
      })
      .toPromise();
    if (response.error) {
      throw new Error(`Failed to update lessons: ${response.error.message}`);
    }
    const lesson = this.convertLessonResponseToTableType(response.data?.setLessonsFlag);
    runInAction(() => {
      if (lesson && lesson.length > 0) {
        this.lessons.set(lesson[0].uuid, { ...this.lessons.get(lesson[0].uuid), ...lesson[0] });
      }
    });
  }

  async deactivateLesson(lessonUuid: string, versionUuid: string) {
    const versionLesson = await this.lessonRequest({ uuids: [lessonUuid] });
    if (versionLesson) {
      await this.removeLessonFromVersionRequest(lessonUuid, versionUuid);
    }
    runInAction(() => {
      this.removeLesson(lessonUuid);
    });
  }

  async deleteLesson(lessonUuid: string, versionUuid: string) {
    const lessonToDelete = await this.lessonRequest({ uuids: [lessonUuid] });
    if (!lessonToDelete?.length || (lessonToDelete && !lessonToDelete[0].onlyInTimetableVersion)) {
      throw new Error(`Lesson with UUID ${lessonUuid} is not only in the timetable version and cannot be deleted.`);
    }

    const deletedCards = await this.deleteCardsRequest(versionUuid, lessonUuid);
    await this.deleteLessonRequest(lessonUuid);

    runInAction(() => {
      this.removeLesson(lessonUuid);
    });
    return { deletedCards, deletedLesson: lessonToDelete };
  }

  /** Kopiert eine Zuordnung der Version samt ihrer Karten (ungesteckt); Einheiten werden nicht kopiert. */
  async duplicateLesson(lessonUuid: string, versionUuid: string) {
    const _versionLesson = await this.lessonRequest({ uuids: [lessonUuid] });
    if (_versionLesson) {
      const versionLesson = _versionLesson[0];
      const { data } = await this.getCardsRequest(versionUuid, lessonUuid);
      const cards = data?.timetableCardsForLesson;
      const newLesson = await this.createLessonInVersionRequest({
        versionUuid,
        schoolYearUuid: versionLesson.schoolYearUuid ?? '',
        input: { ...toLessonInput(toLessonFormValues(versionLesson)), lessonUnits: [] },
        cardDurations: cards?.map((card) => card.duration ?? 1) ?? [],
      });
      runInAction(() => {
        this.addLesson(newLesson[0]);
      });
    }
  }

  async mergeLessons({
    newCards,
    lessonUuids,
    cardsFromLesson,
    versionUuid,
    organizationUuid,
  }: {
    lessonUuids: string[];
    cardsFromLesson?: string | null;
    newCards?: { duration: number; count: number }[];
    versionUuid: string;
    organizationUuid: string;
  }) {
    const mergedLessons = await this.mergeLessonsRequest({
      timetableVersion: versionUuid,
      cardsFromLesson: cardsFromLesson ?? null,
      lessonUuids: lessonUuids,
      newCards: newCards ?? [],
    });
    if (mergedLessons) {
      runInAction(() => {
        this.removeLessons(lessonUuids);
        this.addLesson(mergedLessons[0]);
      });
      return mergedLessons[0];
    }
  }

  async moveLessonToVersion(lessonUuid: string, versionUuid: string, organizationUuid: string) {
    const _versionLesson = await this.lessonRequest({ uuids: [lessonUuid] });

    if (_versionLesson) {
      const newLessons = await this.copyLessonsToTimetableVersionRequest({
        lessonUuids: [lessonUuid],
        timetableVersionUuid: versionUuid,
        organizationUuid,
      });
      if (newLessons) {
        runInAction(() => {
          this.removeLesson(lessonUuid);
          this.addLesson(newLessons[0]);
        });
        return newLessons[0];
      }
    }
    throw new Error(`Lesson with UUID ${lessonUuid} not found`);
  }

  async detachLesson(lessonUuid: string) {
    const response = await urqlClient
      .mutation<DetachTeachersFromLessonsMutation, DetachTeachersFromLessonsMutationVariables>(
        DetachTeachersFromLessonsDocument,
        { uuids: [lessonUuid] },
      )
      .toPromise();
    if (response.error) {
      throw new Error(`Failed to update lessons: ${response.error.message}`);
    }
    runInAction(() => {
      const lesson = this.lessons.get(lessonUuid);
      if (lesson) {
        this.lessons.set(lessonUuid, { ...lesson, teachers: [] });
      }
    });
  }

  async assignLessons(
    lessonUuids: string[],
    teachers: {
      uuid: string;
      name: string | null;
      lastName: string | null;
      color?: string | null;
    }[],
  ) {
    const response = await urqlClient
      .mutation<AssignTeachersToLessonsMutation, AssignTeachersToLessonsMutationVariables>(
        AssignTeachersToLessonsDocument,
        {
          uuids: lessonUuids,
          teachers: teachers.map((teacher) => ({
            personUuid: teacher.uuid,
            present: true,
            teachingLoad: true,
            writesCertificate: true,
          })),
        },
      )
      .toPromise();
    if (response.error) {
      throw new Error(`Failed to update lessons: ${response.error.message}`);
    }
    const lessonsData = this.convertLessonResponseToTableType(response.data?.assignTeachersToLessons);
    runInAction(() => {
      lessonsData.forEach((l) => {
        this.lessons.set(l.uuid, { ...l });
      });
    });
  }

  /** Erst die Karten der Zuordnung in der Version löschen, dann die Zuordnung aus der Version lösen. */
  async removeLessonFromVersionRequest(lessonUuid: string, versionUuid: string) {
    const deletedCards = await this.deleteCardsRequest(versionUuid, lessonUuid);
    const response = await urqlClient
      .mutation<DetachLessonsFromTimetableVersionMutation, DetachLessonsFromTimetableVersionMutationVariables>(
        DetachLessonsFromTimetableVersionDocument,
        { versionUuid, uuids: [lessonUuid] },
      )
      .toPromise();
    if (response.error) {
      throw new Error(`Failed to update lessons: ${response.error.message}`);
    }
    const removedLessons = this.convertLessonResponseToTableType(response.data?.detachLessonsFromTimetableVersion);
    return { deletedCards, removedLessons };
  }

  async copyLessonsToTimetableVersionRequest(input: CopyLessonsToTimetableVersionMutationVariables) {
    const res = await urqlClient.mutation<
      CopyLessonsToTimetableVersionMutation,
      CopyLessonsToTimetableVersionMutationVariables
    >(CopyLessonsToTimetableVersionDocument, { ...input }, { additionalTypenames: LESSON_LIST_TYPENAMES });
    if (res.error || !res.data?.copyLessonsToTimetableVersion) {
      throw new Error(`Failed to copy lessons: ${res.error?.message}`);
    }
    const uuids = res.data.copyLessonsToTimetableVersion.nodeUuids;
    const newLessons = await this.lessonRequest({ uuids });
    if (!newLessons) {
      throw new Error('No lessons found after copying to timetable version');
    }
    return lessonResponseToTableType({
      queryLessonsData: newLessons,
      deputateByTeachersData: [], // TODO: add from backend-resolver
      versionContext: true,
      isTeachingBlock: false,
      subjectsData: this._subjects,
      subjectContainerData: this._subjectContainers,
    });
  }

  async mergeLessonsRequest(input: MergeLessonsMutationVariables) {
    const res = await urqlClient
      .mutation<MergeLessonsMutation, MergeLessonsMutationVariables>(
        MergeLessonsDocument,
        { ...input },
        { additionalTypenames: LESSON_LIST_TYPENAMES },
      )
      .toPromise();
    if (res.error || !res.data?.mergeLessons) {
      throw new Error(`Failed to merge lessons: ${res.error?.message}`);
    }
    const uuids = res.data?.mergeLessons.nodeUuids;
    const newLessons = await this.lessonRequest({ uuids });
    if (!newLessons) {
      throw new Error('No lessons found after copying to timetable version');
    }
    return lessonResponseToTableType({
      queryLessonsData: newLessons,
      deputateByTeachersData: [], // TODO: add from backend-resolver
      versionContext: true,
      isTeachingBlock: false,
      subjectsData: this._subjects,
      subjectContainerData: this._subjectContainers,
    });
  }

  async createLessonInVersionRequest(variables: CreateLessonInTimetableVersionMutationVariables) {
    const res = await urqlClient
      .mutation<CreateLessonInTimetableVersionMutation, CreateLessonInTimetableVersionMutationVariables>(
        CreateLessonInTimetableVersionDocument,
        variables,
      )
      .toPromise();
    if (res.error || !res.data) {
      throw new Error(`Failed to create lessons: ${res.error?.message}`);
    }
    return this.convertLessonResponseToTableType([res.data.createLessonInTimetableVersion]);
  }

  convertLessonResponseToTableType(data?: LessonItemFragment[]): VersionLessonTableType[] {
    return lessonResponseToTableType({
      queryLessonsData: data,
      deputateByTeachersData: [], // TODO: add from backend-resolver
      versionContext: true,
      isTeachingBlock: false,
      subjectsData: this._subjects,
      subjectContainerData: this._subjectContainers,
    });
  }

  /** Die genannten Zuordnungen, vom Backend sortiert (Klassen, Fach, Lehrer). */
  async lessonRequest({ uuids }: { uuids: string[] }) {
    const { data: queryLessonsData } = await urqlClient
      .query<LessonsByUuidsQuery, LessonsByUuidsQueryVariables>(
        LessonsByUuidsDocument,
        { uuids },
        { requestPolicy: 'network-only' },
      )
      .toPromise();
    return queryLessonsData ? queryLessonsData.lessonsByUuids : null;
  }

  async deleteLessonRequest(lessonUuid: string) {
    await urqlClient
      .mutation<DeleteLessonsMutation, DeleteLessonsMutationVariables>(
        DeleteLessonsDocument,
        { uuids: [lessonUuid] },
        { additionalTypenames: LESSON_LIST_TYPENAMES },
      )
      .toPromise();
    return lessonUuid;
  }

  async deleteCardsRequest(versionUuid: string, lessonUuid: string) {
    const cardsToDelete = (await this.getCardsRequest(versionUuid, lessonUuid)).data?.timetableCardsForLesson ?? [];
    if (cardsToDelete.length > 0) {
      await urqlClient
        .mutation<DeleteTimetableCardsMutation, DeleteTimetableCardsMutationVariables>(DeleteTimetableCardsDocument, {
          cardUuids: cardsToDelete.map((c) => c.uuid),
        })
        .toPromise();
    }
    return cardsToDelete;
  }

  async getCardsRequest(versionUuid: string, lessonUuid: string) {
    return await urqlClient
      .query<TimetableCardsForLessonQuery, TimetableCardsForLessonQueryVariables>(
        TimetableCardsForLessonDocument,
        { versionUuid, lessonUuid },
        { requestPolicy: 'network-only' },
      )
      .toPromise();
  }
}
